import React, { useState, useEffect, useCallback } from 'react';
import { Card, Spin, Alert, Empty, App } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';

import { csaService } from '../../services/dashboard/csa.service';
import {
  DashboardCSAData,
  VulnerabilidadeCSA,
  FiltrosVulnerabilidades,
  SortFields,
  SortOrder
} from '../../types/dashboard/csa.types';

import { GlobalContext } from "../../context/globalContext";
import { GlobalContextType } from "../../@types/global";

import CSAPageHeader from '../../components/dashboards/Cybersecurity/CSAPageHeader';
import CSADashboardStats from '../../components/dashboards/Cybersecurity/CSADashboardStats';
import CSASeverityChart from '../../components/dashboards/Cybersecurity/CSASeverityChart';
import CSATimelineChart from '../../components/dashboards/Cybersecurity/CSATimelineChart';
import CSATopLists from '../../components/dashboards/Cybersecurity/CSATopLists';
import CSAFilters from '../../components/dashboards/Cybersecurity/CSAFilters';
import CSAVulnerabilityTable from '../../components/dashboards/Cybersecurity/CSAVulnerabilityTable';
import CSAVulnerabilityModal from '../../components/dashboards/Cybersecurity/CSAVulnerabilityModal';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

interface CSAPageProps {
  // Interface vazia - usando contexto global
}

const CSAPageContent: React.FC<CSAPageProps> = () => {
  const { currentCliente } = React.useContext(GlobalContext) as GlobalContextType;

  const [dashboardData, setDashboardData] = useState<DashboardCSAData | null>(null);
  const [vulnerabilidades, setVulnerabilidades] = useState<VulnerabilidadeCSA[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingVulnerabilidades, setLoadingVulnerabilidades] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filtros, setFiltros] = useState<FiltrosVulnerabilidades>({
    sortBy: SortFields.CREATED_AT,
    sortOrder: SortOrder.DESC
  });

  const [selectedVulnerabilidade, setSelectedVulnerabilidade] = useState<VulnerabilidadeCSA | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);

  const carregarDashboard = useCallback(async () => {
    if (!currentCliente?._id) {
      setLoading(false);
      setError('Nenhum cliente selecionado');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await csaService.getDashboardStats(currentCliente._id);
      setDashboardData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }, [currentCliente?._id]);

  const carregarVulnerabilidades = useCallback(async () => {
    if (!currentCliente?._id) {
      return;
    }

    try {
      setLoadingVulnerabilidades(true);
      const filtrosCompletos = {
        ...filtros,
        page: currentPage,
        limit: pageSize
      };
      
      const response = await csaService.getVulnerabilidades(currentCliente._id, filtrosCompletos);
      setVulnerabilidades(response.vulnerabilidades);
      setTotal(response.paginacao.total);
    } catch (err) {
      setVulnerabilidades([]);
    } finally {
      setLoadingVulnerabilidades(false);
    }
  }, [currentCliente?._id, filtros, currentPage, pageSize]);

  useEffect(() => {
    if (currentCliente?._id) {
      carregarDashboard();
    }
  }, [currentCliente?._id, carregarDashboard]);

  useEffect(() => {
    if (currentCliente?._id) {
      carregarVulnerabilidades();
    }
  }, [carregarVulnerabilidades]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    
    if (sorter.field) {
      setFiltros(prev => ({
        ...prev,
        sortBy: sorter.field,
        sortOrder: sorter.order === 'ascend' ? SortOrder.ASC : SortOrder.DESC
      }));
    }
  };

  const handleSearch = (value: string) => {
    setFiltros(prev => ({ ...prev, search: value }));
    setCurrentPage(1);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFiltros(prev => {
      const newFiltros = { ...prev };
      
      if (!value || value === '') {
        delete newFiltros[key as keyof FiltrosVulnerabilidades];
      } else {
        newFiltros[key as keyof FiltrosVulnerabilidades] = value;
      }
      
      return newFiltros;
    });
    setCurrentPage(1);
  };

  const handleVerDetalhes = async (vulnerabilidade: VulnerabilidadeCSA) => {
    if (!currentCliente?._id) return;
    
    try {
      const detalhes = await csaService.getVulnerabilidade(vulnerabilidade.id, currentCliente._id);
      setSelectedVulnerabilidade(detalhes);
      setModalVisible(true);
    } catch (err) {
      // Silently handle error
    }
  };

  const handleSeverityClick = (severity: string) => {
    if (severity === 'clear_all') {
      setFiltros({
        sortBy: SortFields.CREATED_AT,
        sortOrder: SortOrder.DESC
      });
    } else {
      setFiltros(prev => ({ 
        ...prev, 
        severity: severity 
      }));
    }
    
    setCurrentPage(1);
    
    const vulnerabilidadesSection = document.getElementById('vulnerabilidades-section');
    if (vulnerabilidadesSection) {
      vulnerabilidadesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleHostClick = (host: string) => {
    setFiltros(prev => ({ 
      ...prev, 
      host: host 
    }));
    setCurrentPage(1);
    
    const vulnerabilidadesSection = document.getElementById('vulnerabilidades-section');
    if (vulnerabilidadesSection) {
      vulnerabilidadesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCveClick = (cve: string) => {
    setFiltros(prev => ({ 
      ...prev, 
      search: cve
    }));
    setCurrentPage(1);
    
    const vulnerabilidadesSection = document.getElementById('vulnerabilidades-section');
    if (vulnerabilidadesSection) {
      vulnerabilidadesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleReload = () => {
    carregarDashboard();
    carregarVulnerabilidades();
  };

  if (!currentCliente) {
    return (
      <Alert
        message="Nenhum cliente selecionado"
        description="Por favor, selecione um cliente para visualizar o dashboard CSA."
        type="warning"
        showIcon
      />
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          Carregando dados do CSA...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Erro ao carregar dados"
        description={error}
        type="error"
        showIcon
        action={
          <button onClick={handleReload}>
            Tentar novamente
          </button>
        }
      />
    );
  }

  if (!dashboardData) {
    return (
      <Empty
        description="Nenhum dado disponível"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <CSAPageHeader
        clienteId={currentCliente._id}
        clienteNome={dashboardData.cliente.nome}
        ultimaAtualizacao={dashboardData.ultimaAtualizacao}
        onReload={handleReload}
        dashboardData={dashboardData}
        loading={loading}
      />

      <div style={{ marginBottom: 24 }}>
        <CSADashboardStats 
          estatisticas={dashboardData.estatisticasGerais}
          onSeverityClick={handleSeverityClick}
        />
      </div>

      <div style={{ marginBottom: 24 }}>
        <div className="ant-row" style={{ marginLeft: '-8px', marginRight: '-8px' }}>
          <div className="ant-col ant-col-xs-24 ant-col-lg-12" style={{ paddingLeft: '8px', paddingRight: '8px' }}>
            <CSASeverityChart 
              data={dashboardData.analisePorSeveridade}
              loading={loading}
              onSeverityClick={handleSeverityClick}
            />
          </div>
          <div className="ant-col ant-col-xs-24 ant-col-lg-12" style={{ paddingLeft: '8px', paddingRight: '8px' }}>
            <CSATimelineChart 
              data={dashboardData.analiseTemporal}
              loading={loading}
            />
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <CSATopLists
          hostsVulneraveis={dashboardData.hostsVulneraveis}
          topVulnerabilidades={dashboardData.topVulnerabilidades}
          loading={loading}
          onHostClick={handleHostClick}
          onCveClick={handleCveClick}
        />
      </div>

      <Card title="Vulnerabilidades Detectadas" size="small" id="vulnerabilidades-section">
        <CSAFilters
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          onToggleAdvancedFilters={() => setFilterVisible(!filterVisible)}
          showAdvancedFilters={filterVisible}
          loading={loadingVulnerabilidades}
          activeSeverity={filtros.severity}
          activeHost={filtros.host}
          activeSearch={filtros.search}
        />

        <CSAVulnerabilityTable
          data={vulnerabilidades}
          loading={loadingVulnerabilidades}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total
          }}
          onTableChange={handleTableChange}
          onViewDetails={handleVerDetalhes}
        />
      </Card>

      <CSAVulnerabilityModal
        visible={modalVisible}
        vulnerability={selectedVulnerabilidade}
        onClose={() => setModalVisible(false)}
      />
    </div>
  );
};

const CSAPage: React.FC<CSAPageProps> = () => {
  return (
    <App>
      <CSAPageContent />
    </App>
  );
};

export default CSAPage;